import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Operators from "./Operators";
import LoadingOverlay from "./LoadingOverlay";
import { toast } from "react-toastify";

const API = "https://eps.elkomprintama.com/";

const TABS = [
  { label: "Jobsheet", statusId: 1, key: "JOBSHEET", color: "blue" },
  { label: "On Process", statusId: 2, key: "ON PROCESS", color: "green" },
  { label: "QC Check", statusId: 3, key: "QC CHECK", color: "yellow" },
  { label: "Rework", statusId: 4, key: "REWORK", color: "red" },
];

const STATUS_OPTIONS = [
  {
    id: 1,
    label: "Jobsheet",
    cls: "bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200",
  },
  {
    id: 2,
    label: "On Process",
    cls: "bg-green-100 text-green-700 border-green-300 hover:bg-green-200",
  },
  {
    id: 3,
    label: "QC Check",
    cls: "bg-yellow-100 text-yellow-700 border-yellow-300 hover:bg-yellow-200",
  },
  {
    id: 4,
    label: "Rework",
    cls: "bg-red-100 text-red-700 border-red-300 hover:bg-red-200",
  },
];

function tabStyle(color, active) {
  const base =
    "px-5 py-2 text-sm font-semibold rounded-t-lg transition focus:outline-none";
  if (!active)
    return `${base} text-gray-500 hover:text-gray-700 hover:bg-gray-100`;
  const map = {
    blue: "bg-blue-600 text-white",
    green: "bg-green-600 text-white",
    yellow: "bg-yellow-500 text-white",
    red: "bg-red-500 text-white",
  };
  return `${base} ${map[color]}`;
}

function statusBadge(status) {
  switch (status) {
    case "JOBSHEET":
      return "bg-blue-500 text-white";
    case "ON PROCESS":
      return "bg-green-500 text-white";
    case "QC CHECK":
      return "bg-yellow-500 text-white";
    case "FINISHED GOOD":
      return "bg-gray-500 text-white";
    case "REWORK":
      return "bg-red-500 text-white";
    default:
      return "bg-gray-300 text-gray-700";
  }
}

function AssignModal({ order, onClose, onSaved }) {
  const [oprSelected, setOprSelected] = useState("");
  const [statusSelected, setStatus] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (order) {
      setOprSelected(order.opr_id ?? "");
      setStatus(order.status_id ?? 1);
    }
  }, [order]);

  if (!order) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.patch(`${API}patch/api/orders/`, {
        order_id: order.order_id,
        operator: oprSelected,
        status_id: statusSelected,
        dateOrder: order.date?.split("T")[0] ?? order.date,
        delivery: order.dlvr_date?.split("T")[0] ?? order.dlvr_date,
        qc: order.qc_id,
        qty: order.qty,
        po_number: order.po_num,
      });
      toast.success("Order updated.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-sm font-bold text-gray-800">
              Production Assignment
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Order ID: {order.order_id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSave} className="px-5 py-4 space-y-4">
          {/* Read-only info */}
          <div>
            <p className="text-xs text-gray-400 uppercase font-semibold mb-0.5">
              Customer
            </p>
            <p className="text-sm font-medium text-gray-700">{order.nme}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 uppercase font-semibold mb-0.5">
              Jobsheet
            </p>
            <p className="text-sm font-medium text-gray-700">
              {order.job_name}
            </p>
          </div>
          <div className="flex gap-6">
            <div>
              <p className="text-xs text-gray-400 uppercase font-semibold mb-0.5">
                Size
              </p>
              <p className="text-sm font-medium text-gray-700">{order.size}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase font-semibold mb-0.5">
                QC
              </p>
              <p className="text-sm font-medium text-gray-700">
                {order.qc_name || "—"}
              </p>
            </div>
          </div>

          {/* Status selector */}
          <div>
            <p className="text-xs text-gray-500 uppercase font-semibold tracking-wide mb-2">
              Status
            </p>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStatus(s.id)}
                  className={`flex-1 py-2 rounded-lg border text-xs font-bold transition ${s.cls} ${
                    statusSelected === s.id
                      ? "ring-2 ring-offset-1 ring-current opacity-100"
                      : "opacity-60"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Operator */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Operator
            </label>
            <Operators
              onChangeOpr={(e) => setOprSelected(e.target.value)}
              oprData={oprSelected}
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 transition"
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OrderTable({ orders, onRowClick }) {
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const toggleSort = (key) =>
    setSort((s) => ({
      key,
      dir: s.key === key && s.dir === "asc" ? "desc" : "asc",
    }));
  const si = (key) =>
    sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : " ↕";
  const sortedOrders = useMemo(() => {
    if (!sort.key) return orders;
    return [...orders].sort((a, b) => {
      let va = a[sort.key] ?? "";
      let vb = b[sort.key] ?? "";
      const na = Number(va),
        nb = Number(vb);
      if (!isNaN(na) && !isNaN(nb) && va !== "" && vb !== "") {
        va = na;
        vb = nb;
      }
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [orders, sort]);

  if (orders.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400 text-sm italic">
        No orders in this stage.
      </div>
    );
  }

  return (
    <table className="text-sm w-full text-left text-gray-600 border-collapse">
      <thead className="sticky top-0 z-10 text-xs text-center text-white uppercase bg-gray-600">
        <tr>
          <th
            className="whitespace-nowrap px-4 py-3 border-r cursor-pointer select-none hover:bg-gray-500"
            onClick={() => toggleSort("job_status")}
          >
            Status{si("job_status")}
          </th>
          <th
            className="whitespace-nowrap px-3 py-3 border-r text-left cursor-pointer select-none hover:bg-gray-500"
            onClick={() => toggleSort("nme")}
          >
            Customer{si("nme")}
          </th>
          <th
            className="whitespace-nowrap px-3 py-3 border-r text-left cursor-pointer select-none hover:bg-gray-500"
            onClick={() => toggleSort("job_name")}
          >
            Job Name{si("job_name")}
          </th>
          <th
            className="whitespace-nowrap px-3 py-3 border-r cursor-pointer select-none hover:bg-gray-500"
            onClick={() => toggleSort("size")}
          >
            Size{si("size")}
          </th>
          <th
            className="whitespace-nowrap px-3 py-3 border-r cursor-pointer select-none hover:bg-gray-500"
            onClick={() => toggleSort("qty")}
          >
            Qty{si("qty")}
          </th>
          <th
            className="whitespace-nowrap px-3 py-3 border-r cursor-pointer select-none hover:bg-gray-500"
            onClick={() => toggleSort("po_num")}
          >
            PO Number{si("po_num")}
          </th>
          <th
            className="whitespace-nowrap px-3 py-3 border-r cursor-pointer select-none hover:bg-gray-500"
            onClick={() => toggleSort("dlvr_date")}
          >
            Delivery Date{si("dlvr_date")}
          </th>
          <th
            className="whitespace-nowrap px-3 py-3 border-r text-left cursor-pointer select-none hover:bg-gray-500"
            onClick={() => toggleSort("opr_name")}
          >
            Operator{si("opr_name")}
          </th>
          <th
            className="whitespace-nowrap px-3 py-3 text-left cursor-pointer select-none hover:bg-gray-500"
            onClick={() => toggleSort("qc_name")}
          >
            QC{si("qc_name")}
          </th>
        </tr>
      </thead>
      <tbody>
        {sortedOrders.map((order, i) => (
          <tr
            key={i}
            className={`border-b hover:bg-blue-50 cursor-pointer ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
            onDoubleClick={() => onRowClick(order)}
          >
            <td className="px-4 py-2 text-center border-r">
              <span
                className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${statusBadge(order.job_status)}`}
              >
                {order.job_status}
              </span>
            </td>
            <td className="whitespace-nowrap px-3 py-2 border-r">
              {order.nme}
            </td>
            <td className="whitespace-nowrap px-3 py-2 border-r">
              {order.job_name}
            </td>
            <td className="whitespace-nowrap px-3 py-2 border-r text-center">
              {order.size}
            </td>
            <td className="whitespace-nowrap px-3 py-2 border-r text-center">
              {order.qty}
            </td>
            <td className="whitespace-nowrap px-3 py-2 border-r">
              {order.po_num}
            </td>
            <td className="whitespace-nowrap px-3 py-2 border-r text-center">
              {new Date(order.dlvr_date).toLocaleDateString()}
            </td>
            <td className="whitespace-nowrap px-3 py-2 border-r">
              {order.opr_name}
            </td>
            <td className="whitespace-nowrap px-3 py-2">{order.qc_name}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ProductionAssignment() {
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selected, setSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}get/api/orders`);
      setOrders(res.data.result || []);
    } catch {
      toast.error("Failed to load orders.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const currentTab = TABS[activeTab];
  const filteredOrders = orders.filter((o) => o.job_status === currentTab.key);

  return (
    <>
      <div className="w-full rounded-lg bg-green-600 mb-4">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Production Assignment
        </label>
      </div>

      <div
        className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col relative"
        style={{ maxHeight: "calc(100vh - 160px)" }}
      >
        {isLoading && <LoadingOverlay message="Loading" />}

        {/* Tab bar */}
        <div className="flex border-b border-gray-200 px-4 pt-3 gap-1 bg-gray-50 flex-shrink-0">
          {TABS.map((tab, i) => {
            const count = orders.filter((o) => o.job_status === tab.key).length;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(i)}
                className={tabStyle(tab.color, activeTab === i)}
              >
                {tab.label}
                <span
                  className={`ml-2 text-xs rounded-full px-1.5 py-0.5 ${
                    activeTab === i
                      ? "bg-white/30"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-auto">
          <OrderTable orders={filteredOrders} onRowClick={setSelected} />
        </div>
      </div>

      <AssignModal
        order={selected}
        onClose={() => setSelected(null)}
        onSaved={fetchOrders}
      />
    </>
  );
}

export default ProductionAssignment;
